import { z } from "zod";

// TES-1634 (TES-1632, Emelie 2026-10-28, "KANONISKT BESLUT 2026-10-28 —
// MÅNADSINCHECKNING OCH RECEPTFÖRNYELSE") — "Nästa receptförnyelse".
//
//   "Dina steg används endast för den linjära resan fram till
//    behandlingsstart och tas bort helt när behandlingen är aktiv."
//   "Under aktiv behandling ersätts den av den återkommande komponenten
//    Nästa receptförnyelse med lägena: Månadsincheckning öppnar {datum}; Gör
//    månadsincheckningen; Läkaren granskar din receptförnyelse; Du får
//    läkarens besked; Ditt nya recept är utfärdat eller Ditt nuvarande recept
//    gäller fortfarande."
//   "Bara aktuellt läge är utvecklat; kommande lägen är nedtonade.
//    Komponenten nollställs när cykeln är avslutad."
//
// The server derives the status (apps/api cycle/prescription-renewal.ts);
// the app only renders it.
//
// `kind` is the decision's split between a real renewal and a routine
// monthly follow-up:
//   "Systemet skiljer på monthly_review_due och prescription_renewal_due."
//   "Om recepttäckningen inte kan fastställas används den neutrala
//    månadsuppföljningscopyn; systemet får inte påstå att receptförnyelse
//    pågår utan säkert underlag."
// So `renewal` only when the prescription she holds is known to run out
// before the dose two after the F-05 ("Du har två planerade doser kvar innan
// nästa recept behövs"); `follow_up` when it is known to cover longer, or
// when its coverage cannot be told.
export const PRESCRIPTION_RENEWAL_KINDS = ["renewal", "follow_up"] as const;
export const prescriptionRenewalKindSchema = z.enum(PRESCRIPTION_RENEWAL_KINDS);
export type PrescriptionRenewalKind = z.infer<
  typeof prescriptionRenewalKindSchema
>;

// The statuses in their order. `new_issued` and `existing_valid` are the two
// final states ("Ditt nya recept är utfärdat" / "Ditt nuvarande recept
// gäller fortfarande"), set only on the doctor's recorded prescription
// choice, never assumed.
export const PRESCRIPTION_RENEWAL_STATUSES = [
  "upcoming",
  "checkin_open",
  "under_review",
  "decision_ready",
  "new_issued",
  "existing_valid",
] as const;
export const prescriptionRenewalStatusSchema = z.enum(
  PRESCRIPTION_RENEWAL_STATUSES,
);
export type PrescriptionRenewalStatus = z.infer<
  typeof prescriptionRenewalStatusSchema
>;

export const prescriptionRenewalSchema = z.object({
  kind: prescriptionRenewalKindSchema,
  status: prescriptionRenewalStatusSchema,
  // The day the next F-05 opens (YYYY-MM-DD, Stockholm): the {datum} of
  // "Månadsincheckning öppnar {datum}". Set only with `upcoming`.
  opensOn: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable(),
  // The open F-05 (carebyella://milestone/<n>), with `checkin_open` only.
  checkinPath: z.string().nullable(),
});
export type PrescriptionRenewal = z.infer<typeof prescriptionRenewalSchema>;

// The week-3 card, verbatim from the 2026-10-28 decision ("Copy när
// förnyelse behövs"). English derived from the approved Swedish.
export const RENEWAL_DUE_CARD_COPY = {
  title: {
    sv: "Dags att förbereda din receptförnyelse",
    en: "Time to prepare your prescription renewal",
  },
  body: {
    sv: "Du har två planerade doser kvar innan nästa recept behövs. Gör månadsincheckningen så att läkaren får underlag för att bedöma din receptförnyelse i god tid.",
    en: "You have two planned doses left before your next prescription is needed. Do the monthly check-in so that the doctor has what they need to assess your prescription renewal in good time.",
  },
  cta: { sv: "Gör månadsincheckningen", en: "Do the monthly check-in" },
  howItWorks: {
    sv: "Så fungerar receptförnyelsen",
    en: "How the prescription renewal works",
  },
} as const;

// The F-05 card when no renewal is due: CP-30's "Vad händer just nu" copy,
// which promises no prescription ("monthly_review_due får inte i sig
// utlova nytt recept"). English derived.
export const MONTHLY_REVIEW_CARD_COPY = {
  title: {
    sv: "Din månadsincheckning är öppen",
    en: "Your monthly check-in is open",
  },
  body: {
    sv: "Svara på frågorna så att läkaren får ett aktuellt underlag inför månadens medicinska bedömning.",
    en: "Answer the questions so that the doctor has up-to-date information for this month's medical assessment.",
  },
  cta: { sv: "Gör månadsincheckningen", en: "Do the monthly check-in" },
} as const;

// "Så fungerar receptförnyelsen" opens this explanation: "Den sekundära
// länken öppnar en kompakt expansion eller bottom sheet, inte en
// DT-artikel". The app renders it (RenewalExplainerScreen); the path is
// served only to a build at CLIENT_CONTRACT_CHANGES.renewalExplainer.
export const RENEWAL_EXPLAINER_PATH = "carebyella://renewal/how-it-works";
